import React, { useState } from "react";
import { motion } from "framer-motion";
import { DevUpdateImage } from "../../../store/types/development-updates-types";

const baseUrl =
  process.env.REACT_APP_STRAPI_IMAGES_URL?.replace(/\/$/, "") || "";

interface Props {
  images: DevUpdateImage[];
  videoLink?: string;
}

const getVimeoId = (url: string): string => {
  const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return match ? match[1] : "";
};

const getImageUrl = (img: DevUpdateImage): string => {
  if (img.url.startsWith("http")) {
    return img.url;
  }
  return baseUrl + (img.url.startsWith("/") ? img.url : `/${img.url}`);
};

const getThumbnailUrl = (img: DevUpdateImage): string => {
  if (img.formats?.thumbnail?.url) {
    const url = img.formats.thumbnail.url;
    return url.startsWith("http") ? url : baseUrl + (url.startsWith("/") ? url : `/${url}`);
  }
  if (img.formats?.small?.url) {
    const url = img.formats.small.url;
    return url.startsWith("http") ? url : baseUrl + (url.startsWith("/") ? url : `/${url}`);
  }
  return getImageUrl(img);
};

const getMainImageUrl = (img: DevUpdateImage): string => {
  // Use medium format for main display, fallback to small, then original
  if (img.formats?.medium?.url) {
    const url = img.formats.medium.url;
    return url.startsWith("http") ? url : baseUrl + (url.startsWith("/") ? url : `/${url}`);
  }
  if (img.formats?.small?.url) {
    const url = img.formats.small.url;
    return url.startsWith("http") ? url : baseUrl + (url.startsWith("/") ? url : `/${url}`);
  }
  return getImageUrl(img);
};

type MediaItem = 
  | { type: "video"; url: string; id: number }
  | { type: "image"; url: string; thumbnailUrl: string; id: number; image: DevUpdateImage };

const DevUpdateImageGallery: React.FC<Props> = ({ images, videoLink }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [fullscreenMedia, setFullscreenMedia] = useState<string | null>(null);

  // Combine video and images into a single media array
  const mediaItems: MediaItem[] = [
    ...(videoLink ? [{ type: "video" as const, url: videoLink, id: -1 }] : []),
    ...(images?.map((img) => ({
      type: "image" as const,
      url: getMainImageUrl(img),
      thumbnailUrl: getThumbnailUrl(img),
      id: img.id,
      image: img,
    })) || []),
  ];

  if (mediaItems.length === 0) {
    return (
      <div className="bg-gray-100 rounded-lg p-8 text-center text-gray-500">
        No media available
      </div>
    );
  }

  const currentMedia = mediaItems[selectedIndex];

  return (
    <div className="w-full h-full flex flex-col">
      {/* Main Media Display */}
      <div className="w-full mb-4 rounded-lg overflow-hidden bg-gray-100 flex-1 flex items-center justify-center" style={{ minHeight: "400px", maxHeight: "400px" }}>
        {currentMedia.type === "video" ? (
          <motion.div
            key={selectedIndex}
            className="relative w-full aspect-video"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <iframe
              className="w-full h-full rounded-lg"
              src={`https://player.vimeo.com/video/${getVimeoId(currentMedia.url)}`}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title="Development Update Video"
              onLoad={() => setIsVideoPlaying(true)}
            />
            {!isVideoPlaying && (
              <div
                className="absolute inset-0 flex justify-center items-center bg-black bg-opacity-40 cursor-pointer"
                onClick={() => setIsVideoPlaying(true)}
              >
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-lg">
                  <div
                    className="w-0 h-0 border-l-[12px] border-t-[8px] border-b-[8px] border-transparent border-l-black"
                    style={{ marginLeft: "3px" }}
                  />
                </div>
              </div>
            )}
          </motion.div>
        ) : (
          <motion.img
            key={selectedIndex}
            src={currentMedia.url}
            alt={
              currentMedia.image?.alternativeText ||
              currentMedia.image?.caption ||
              "Development Update"
            }
            className="w-full h-full object-contain cursor-pointer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            loading="lazy"
            onClick={() => setFullscreenMedia(currentMedia.url)}
          />
        )}
      </div>

      {/* Thumbnail List - Horizontal Scroll */}
      {mediaItems.length > 1 && (
        <div className="w-full overflow-x-auto scrollbar-hide py-4">
          <div className="flex gap-3 pb-2 px-4" style={{ minWidth: "min-content" }}>
            {mediaItems.map((media, index) => {
              const isSelected = index === selectedIndex;

              return (
                <button
                  key={media.id}
                  onClick={(e) => {
                    setSelectedIndex(index);
                    setIsVideoPlaying(false);
                    // Scroll into view
                    e.currentTarget.scrollIntoView({
                      behavior: "smooth",
                      block: "nearest",
                      inline: "center",
                    });
                  }}
                  className={`relative overflow-hidden rounded-lg transition-all flex-shrink-0 border-2 ${
                    isSelected
                      ? "border-blue-500"
                      : "border-transparent opacity-70 hover:opacity-100 hover:border-gray-300"
                  }`}
                  style={{ width: "100px", height: "100px" }}
                >
                  {media.type === "video" ? (
                    <div className="relative w-full h-full">
                      <iframe
                        className="w-full h-full object-cover rounded-md pointer-events-none"
                        src={`https://player.vimeo.com/video/${getVimeoId(
                          media.url
                        )}?background=1&autoplay=0&muted=1&playsinline=1&controls=0`}
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                        title="Video Thumbnail"
                      />
                      <div className="absolute inset-0 flex justify-center items-center">
                        <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-lg">
                          <div
                            className="w-0 h-0 border-l-[6px] border-t-[4px] border-b-[4px] border-transparent border-l-black"
                            style={{ marginLeft: "2px" }}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <img
                      src={media.thumbnailUrl}
                      alt={
                        media.image?.alternativeText ||
                        media.image?.caption ||
                        `Thumbnail ${index + 1}`
                      }
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Fullscreen Modal */}
      {fullscreenMedia && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-90 flex justify-center items-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setFullscreenMedia(null)}
        >
          <div
            className="absolute top-5 right-5 text-white text-3xl cursor-pointer bg-gray-800 px-3 py-2 rounded-full hover:bg-gray-700 transition-colors z-10"
            onClick={(e) => {
              e.stopPropagation();
              setFullscreenMedia(null);
            }}
          >
            ✖
          </div>
          <img
            src={fullscreenMedia}
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
            alt="Fullscreen"
            onClick={(e) => e.stopPropagation()}
          />
        </motion.div>
      )}
    </div>
  );
};

export default DevUpdateImageGallery;

